import * as actions from './actions'
import * as authActions from '../auth/actions'
import { getAsyncActionMeta } from './asyncActionMeta'
import { BULK_ORDER_GRID_AXES } from './config'
import {
  applyCartFetchError,
  applyCartFetchStart,
  applyCartLinesAfterCatalogRemap,
  applyCartSaveConflict,
  applyCartSaveError,
  applyCartSaveStart,
  applyCartSaveSuccess,
  replaceCartFromServer,
  withCartMutation,
} from './cartReducerHelpers'
import { normalizeCatalogId } from './catalogUtils'
import {
  CART_LOAD_STATUS,
  createCartLine,
  DEFAULT_CART_STATE,
  DEFAULT_GRID_CONTEXT,
  findCartLineIndexByKey,
  getCartLineKey,
  isBulkOrderCartConflictError,
  isValidCartLineQuantity,
  normalizeCartMeta,
  normalizeCartResponse,
  normalizeGridAxis,
  remapCartLinesForCatalog,
  SUBMIT_STATUS,
} from './cartUtils'
import { resolveGridContextForLensDetail, applyGridContextPatch } from './gridContextUtils'
import { DEFAULT_CATALOG_UI, applyCatalogUiPatch } from './catalogUiUtils'

export const createInitialState = () => ({
  catalog: [],
  catalogId: null,
  isCatalogReady: false,
  isCatalogLoading: false,
  hasCatalogError: false,
  selectedLensId: null,
  lensDetail: null,
  isLensDetailLoading: false,
  hasLensDetailError: false,
  gridContext: { ...DEFAULT_GRID_CONTEXT },
  catalogUi: {
    ...DEFAULT_CATALOG_UI,
    expandedGroups: { ...DEFAULT_CATALOG_UI.expandedGroups },
  },
  cart: {
    ...DEFAULT_CART_STATE,
    lines: [],
  },
  catalogCartRemapNotice: null,
  submitStatus: SUBMIT_STATUS.IDLE,
  history: {
    orders: [],
    meta: {},
    loading: false,
    isMasterUser: false,
    slaveUsers: [],
  },
  detail: {
    order: null,
    loading: false,
    error: false,
    notFound: false,
    requestId: null,
  },
})

/** Test / deep-equal baseline only — prefer createInitialState() for live state. */
export const initialState = createInitialState()

const getCartActionViewerId = action => getAsyncActionMeta(action).viewerId ?? null

const getLensDetailLensId = action => getAsyncActionMeta(action).lensId ?? null

const getBulkOrderDetailRequestId = action => getAsyncActionMeta(action).id ?? null

const isCartActionForOwner = (state, action) => {
  const viewerId = getCartActionViewerId(action)
  return viewerId != null && viewerId === state.cart.cartOwnerId
}

const isLensDetailActionForSelection = (state, action) => {
  const lensId = getLensDetailLensId(action)
  if (lensId == null || state.selectedLensId == null) return false
  return String(lensId) === String(state.selectedLensId)
}

const isBulkOrderDetailActionForRequest = (state, action) => {
  const requestId = getBulkOrderDetailRequestId(action)
  if (requestId == null || state.detail?.requestId == null) return false
  return String(requestId) === String(state.detail.requestId)
}

const resolveSelectedLensCode = state => {
  if (state.lensDetail?.code) return state.lensDetail.code
  if (!state.selectedLensId) return null
  const selectedItem = state.catalog.find(
    item => String(item._id) === String(state.selectedLensId),
  )
  return selectedItem?.code ?? null
}

/** Reset diameter/side to defaults; keep cylinder/addition sign prefs. */
const resetGridContextKeepSigns = gridContext => ({
  ...DEFAULT_GRID_CONTEXT,
  cylinderSign: gridContext?.cylinderSign || DEFAULT_GRID_CONTEXT.cylinderSign,
  additionSign: gridContext?.additionSign || DEFAULT_GRID_CONTEXT.additionSign,
})

const selectSameLens = state => state

const deselectLens = state => ({
  ...state,
  selectedLensId: null,
  lensDetail: null,
  isLensDetailLoading: false,
  hasLensDetailError: false,
  gridContext: resetGridContextKeepSigns(state.gridContext),
})

// Keep previous lensDetail/gridContext until FETCH_*_SUCCESS (anti-flash).
const switchLens = (state, nextSelectedLensId) => ({
  ...state,
  selectedLensId: nextSelectedLensId,
  hasLensDetailError: false,
})

const remapSelectionForCatalog = (state, items) => {
  if (!state.selectedLensId) {
    return {
      selectedLensId: null,
      lensDetail: null,
      isLensDetailLoading: false,
      hasLensDetailError: false,
      gridContext: state.gridContext,
    }
  }

  // Prefer matching the current id in the new catalog. Needed when Redux storage restores
  // selectedLensId before first catalog load (old catalog empty, so code lookup fails).
  const matchedById = items.find(
    item => String(item._id) === String(state.selectedLensId),
  )
  if (matchedById) {
    return {
      selectedLensId: String(matchedById._id),
      lensDetail: state.lensDetail,
      isLensDetailLoading: state.isLensDetailLoading,
      hasLensDetailError: state.hasLensDetailError,
      gridContext: state.gridContext,
    }
  }

  const previousCode = resolveSelectedLensCode(state)
  const matchedByCode = previousCode
    ? items.find(item => item.code === previousCode)
    : null

  if (matchedByCode) {
    return {
      selectedLensId: String(matchedByCode._id),
      lensDetail: null,
      isLensDetailLoading: false,
      hasLensDetailError: false,
      gridContext: state.gridContext,
    }
  }

  return {
    selectedLensId: null,
    lensDetail: null,
    isLensDetailLoading: false,
    hasLensDetailError: false,
    gridContext: resetGridContextKeepSigns(state.gridContext),
  }
}

const nextCatalogCartRemapNotice = (state, remapResult) => {
  // Remaps are silent; only notify when products were dropped from the cart.
  if (!remapResult?.droppedCount) return state.catalogCartRemapNotice
  return {
    remappedCount: remapResult.remappedCount,
    droppedCount: remapResult.droppedCount,
    seq: (state.catalogCartRemapNotice?.seq ?? 0) + 1,
  }
}

const applyCatalogCartRemap = (state, catalogItems) => {
  const remapResult = remapCartLinesForCatalog(state.cart.lines, catalogItems)
  if (!remapResult.changed) {
    return {
      cart: state.cart,
      catalogCartRemapNotice: state.catalogCartRemapNotice,
    }
  }

  return {
    cart: applyCartLinesAfterCatalogRemap(state.cart, remapResult),
    catalogCartRemapNotice: nextCatalogCartRemapNotice(state, remapResult),
  }
}

const canRemapCartLines = state =>
  state.cart.loadStatus === CART_LOAD_STATUS.READY &&
  state.submitStatus !== SUBMIT_STATUS.SUBMITTING

const reducer = (state = createInitialState(), action) => {
  switch (action.type) {
    case authActions.SIGN_OUT: {
      return createInitialState()
    }

    case actions.FETCH_BULK_ORDER_CATALOG_START: {
      const hasWarmCatalog = state.isCatalogReady
      return {
        ...state,
        isCatalogLoading: !hasWarmCatalog,
        hasCatalogError: false,
      }
    }
    case actions.FETCH_BULK_ORDER_CATALOG_SUCCESS: {
      const { data } = action.payload
      const items = Array.isArray(data?.items) ? data.items : []
      const catalogId = normalizeCatalogId(data?.catalogId)
      const selection = remapSelectionForCatalog(state, items)
      const cartRemap = canRemapCartLines(state)
        ? applyCatalogCartRemap(state, items)
        : {
            cart: state.cart,
            catalogCartRemapNotice: state.catalogCartRemapNotice,
          }

      return {
        ...state,
        catalog: items,
        catalogId,
        isCatalogReady: true,
        isCatalogLoading: false,
        hasCatalogError: false,
        ...selection,
        cart: cartRemap.cart,
        catalogCartRemapNotice: cartRemap.catalogCartRemapNotice,
      }
    }
    case actions.FETCH_BULK_ORDER_CATALOG_ERROR: {
      if (state.isCatalogReady) {
        return {
          ...state,
          isCatalogLoading: false,
        }
      }

      return {
        ...state,
        catalog: [],
        catalogId: null,
        isCatalogReady: false,
        isCatalogLoading: false,
        hasCatalogError: true,
      }
    }

    case actions.SELECT_BULK_ORDER_LENS: {
      const { lensId } = action.payload
      const nextSelectedLensId = lensId == null ? null : String(lensId)

      if (nextSelectedLensId === state.selectedLensId) {
        return selectSameLens(state)
      }

      if (nextSelectedLensId == null) {
        return deselectLens(state)
      }

      return switchLens(state, nextSelectedLensId)
    }

    case actions.FETCH_BULK_ORDER_LENS_DETAIL_START: {
      if (!isLensDetailActionForSelection(state, action)) return state

      return {
        ...state,
        isLensDetailLoading: true,
        hasLensDetailError: false,
      }
    }
    case actions.FETCH_BULK_ORDER_LENS_DETAIL_SUCCESS: {
      if (!isLensDetailActionForSelection(state, action)) return state

      const { data } = action.payload
      const lensDetail =
        data == null
          ? null
          : {
              ...data,
              _id: data._id == null ? data._id : String(data._id),
            }

      return {
        ...state,
        lensDetail,
        isLensDetailLoading: false,
        hasLensDetailError: false,
        gridContext: resolveGridContextForLensDetail(state.gridContext, lensDetail),
      }
    }
    case actions.FETCH_BULK_ORDER_LENS_DETAIL_ERROR: {
      if (!isLensDetailActionForSelection(state, action)) return state

      return {
        ...state,
        lensDetail: null,
        isLensDetailLoading: false,
        hasLensDetailError: true,
      }
    }

    case actions.SET_BULK_ORDER_GRID_CONTEXT: {
      const gridContext = applyGridContextPatch(state.gridContext, action.payload)
      if (gridContext === state.gridContext) return state
      return {
        ...state,
        gridContext,
      }
    }

    case actions.SET_BULK_ORDER_CATALOG_UI: {
      const catalogUi = applyCatalogUiPatch(state.catalogUi, action.payload)
      if (catalogUi === state.catalogUi) return state
      return {
        ...state,
        catalogUi,
      }
    }

    case actions.UPSERT_BULK_ORDER_CART_LINE: {
      const {
        lensId,
        lensCode,
        lensName,
        diameter,
        side,
        sphere,
        cylinder,
        addition = 0,
        gridAxis = BULK_ORDER_GRID_AXES.CYLINDER,
        quantity,
      } = action.payload

      if (!isValidCartLineQuantity(quantity)) {
        return state
      }

      const normalizedLensId = String(lensId)
      const lineKey = getCartLineKey({
        lensId: normalizedLensId,
        diameter,
        side,
        sphere,
        cylinder,
        addition,
      })
      const existingIndex = findCartLineIndexByKey(state.cart.lines, lineKey)

      return withCartMutation(state, cart => {
        if (existingIndex !== -1) {
          const lines = [...cart.lines]
          lines[existingIndex] = {
            ...lines[existingIndex],
            lensCode,
            lensName,
            gridAxis: normalizeGridAxis(gridAxis),
            quantity,
          }
          return { ...cart, lines }
        }

        return {
          ...cart,
          lines: [
            ...cart.lines,
            createCartLine({
              lensId: normalizedLensId,
              lensCode,
              lensName,
              diameter,
              side,
              sphere,
              cylinder,
              addition,
              gridAxis,
              quantity,
            }),
          ],
        }
      })
    }

    case actions.UPDATE_BULK_ORDER_CART_LINE_QUANTITY: {
      const { id, quantity } = action.payload

      if (!isValidCartLineQuantity(quantity)) {
        return state
      }

      const lineIndex = state.cart.lines.findIndex(line => line.id === id)
      if (lineIndex === -1) {
        return state
      }

      return withCartMutation(state, cart => {
        const lines = [...cart.lines]
        lines[lineIndex] = {
          ...lines[lineIndex],
          quantity,
        }
        return { ...cart, lines }
      })
    }

    case actions.REMOVE_BULK_ORDER_CART_LINE: {
      const { id } = action.payload
      return withCartMutation(state, cart => ({
        ...cart,
        lines: cart.lines.filter(line => line.id !== id),
      }))
    }

    case actions.REMOVE_BULK_ORDER_CART_LINES_BY_LENS_ID: {
      const { lensId } = action.payload
      const normalizedLensId = String(lensId)
      return withCartMutation(state, cart => ({
        ...cart,
        lines: cart.lines.filter(line => line.lensId !== normalizedLensId),
      }))
    }

    case actions.SET_BULK_ORDER_CART_META: {
      const { reference, remark } = action.payload
      const { cart } = state
      const nextReference =
        reference !== undefined ? normalizeCartMeta(reference) : cart.reference
      const nextRemark = remark !== undefined ? normalizeCartMeta(remark) : cart.remark

      if (nextReference === cart.reference && nextRemark === cart.remark) {
        return state
      }

      return withCartMutation(state, nextCart => ({
        ...nextCart,
        reference: nextReference,
        remark: nextRemark,
      }))
    }

    case actions.CLEAR_BULK_ORDER_CART:
      return withCartMutation(state, cart => ({
        ...cart,
        lines: [],
        reference: '',
        remark: '',
      }))

    case actions.CLEAR_BULK_ORDER_CATALOG_CART_REMAP_NOTICE:
      return {
        ...state,
        catalogCartRemapNotice: null,
      }

    case actions.FETCH_BULK_ORDER_CART_START: {
      const viewerId = getCartActionViewerId(action)
      return {
        ...state,
        cart: applyCartFetchStart(state.cart, viewerId),
      }
    }

    case actions.FETCH_BULK_ORDER_CART_SUCCESS: {
      if (!isCartActionForOwner(state, action)) return state
      const normalized = normalizeCartResponse(action.payload?.data)
      const replacedCart = replaceCartFromServer(state.cart, normalized)
      const stateAfterReplace = {
        ...state,
        cart: replacedCart,
      }

      if (
        !state.isCatalogReady ||
        state.submitStatus === SUBMIT_STATUS.SUBMITTING ||
        !Array.isArray(state.catalog) ||
        state.catalog.length === 0
      ) {
        return stateAfterReplace
      }

      const cartRemap = applyCatalogCartRemap(stateAfterReplace, state.catalog)
      return {
        ...stateAfterReplace,
        cart: cartRemap.cart,
        catalogCartRemapNotice: cartRemap.catalogCartRemapNotice,
      }
    }

    case actions.FETCH_BULK_ORDER_CART_ERROR: {
      if (!isCartActionForOwner(state, action)) return state
      return {
        ...state,
        cart: applyCartFetchError(state.cart),
      }
    }

    case actions.SAVE_BULK_ORDER_CART_START: {
      if (!isCartActionForOwner(state, action)) return state
      const nextCart = applyCartSaveStart(state.cart)
      if (nextCart === state.cart) return state
      return {
        ...state,
        cart: nextCart,
      }
    }

    case actions.SAVE_BULK_ORDER_CART_SUCCESS: {
      if (!isCartActionForOwner(state, action)) return state
      const normalized = normalizeCartResponse(action.payload?.data)
      return {
        ...state,
        cart: applyCartSaveSuccess(state.cart, normalized),
      }
    }

    case actions.SAVE_BULK_ORDER_CART_ERROR: {
      if (!isCartActionForOwner(state, action)) return state
      // Middleware passes error.errors as payload (API error array).
      if (isBulkOrderCartConflictError({ errors: action.payload })) {
        return {
          ...state,
          cart: applyCartSaveConflict(state.cart),
        }
      }
      return {
        ...state,
        cart: applyCartSaveError(state.cart),
      }
    }

    case actions.SUBMIT_BULK_ORDER_START: {
      if (!isCartActionForOwner(state, action)) return state
      return {
        ...state,
        submitStatus: SUBMIT_STATUS.SUBMITTING,
      }
    }

    case actions.SUBMIT_BULK_ORDER_SUCCESS: {
      if (!isCartActionForOwner(state, action)) return state
      const normalized = normalizeCartResponse(action.payload?.data?.cart)
      return {
        ...state,
        submitStatus: SUBMIT_STATUS.IDLE,
        cart: replaceCartFromServer(state.cart, normalized),
      }
    }

    case actions.SUBMIT_BULK_ORDER_ERROR: {
      if (!isCartActionForOwner(state, action)) return state
      return {
        ...state,
        submitStatus: SUBMIT_STATUS.FAILED,
      }
    }

    case actions.FETCH_BULK_ORDERS_START: {
      return {
        ...state,
        history: {
          ...state.history,
          loading: true,
        },
      }
    }

    case actions.FETCH_BULK_ORDERS_SUCCESS: {
      const { data, meta } = action.payload || {}
      return {
        ...state,
        history: {
          ...state.history,
          orders: data?.orders || [],
          isMasterUser: Boolean(data?.isMasterUser),
          slaveUsers: data?.slaveUsers || [],
          meta: meta || {},
          loading: false,
        },
      }
    }

    case actions.FETCH_BULK_ORDERS_ERROR: {
      return {
        ...state,
        history: {
          ...state.history,
          loading: false,
        },
      }
    }

    case actions.FETCH_BULK_ORDER_START: {
      const requestId = getBulkOrderDetailRequestId(action)
      if (requestId == null) return state

      return {
        ...state,
        detail: {
          order: null,
          loading: true,
          error: false,
          notFound: false,
          requestId,
        },
      }
    }

    case actions.FETCH_BULK_ORDER_SUCCESS: {
      if (!isBulkOrderDetailActionForRequest(state, action)) return state

      return {
        ...state,
        detail: {
          ...state.detail,
          order: action.payload?.data || null,
          loading: false,
          error: false,
          notFound: false,
        },
      }
    }

    case actions.FETCH_BULK_ORDER_ERROR: {
      if (!isBulkOrderDetailActionForRequest(state, action)) return state

      const payload = action.payload
      const notFound = Array.isArray(payload)
        ? payload.some(item => item?.code === 404)
        : payload?.code === 404
      return {
        ...state,
        detail: {
          ...state.detail,
          order: null,
          loading: false,
          error: !notFound,
          notFound,
        },
      }
    }

    case actions.CLEAR_BULK_ORDER_DETAIL: {
      return {
        ...state,
        detail: {
          order: null,
          loading: false,
          error: false,
          notFound: false,
          requestId: null,
        },
      }
    }

    default:
      return state
  }
}

export default reducer
